import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  type AppConfigEntryInput,
  type WebsiteEntry,
  createBillingEntity,
  getAppConfig,
  getBillingEntity,
  getBillingEntityById,
  getWebsiteSettings,
  listBillingEntities,
  setDefaultBillingEntity,
  updateAppConfig,
  updateBillingEntity,
  updateBillingEntityById,
  updateWebsiteSettings,
} from './settings.api';

import type { CreateBillingEntity, UpdateBillingEntitySettings } from '@utazzo/shared';

export const useWebsiteSettings = () =>
  useQuery({ queryKey: ['website-settings'], queryFn: getWebsiteSettings });

export const useAppConfig = () => useQuery({ queryKey: ['app-config'], queryFn: getAppConfig });

export const useUpdateWebsiteSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entries: WebsiteEntry[]) => updateWebsiteSettings(entries),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['website-settings'] });
      toast.success('Website settings saved');
    },
    onError: () => toast.error('Failed to save settings'),
  });
};

export const useUpdateAppConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entries: AppConfigEntryInput[]) => updateAppConfig(entries),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['app-config'] });
      toast.success('Branding & theme saved');
    },
    onError: () => toast.error('Failed to save branding'),
  });
};

export const useBillingEntity = () =>
  useQuery({ queryKey: ['billing-entity'], queryFn: getBillingEntity });

export const useUpdateBillingEntity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateBillingEntitySettings) => updateBillingEntity(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-entity'] });
      toast.success('Company / invoice settings saved');
    },
    onError: () => toast.error('Failed to save company settings'),
  });
};

// ----- Multi-account GST (billing entities) -----

/** All GST accounts. Used by the invoice form and the company settings list. */
export const useBillingEntities = () =>
  useQuery({ queryKey: ['billing-entities'], queryFn: listBillingEntities });

/** One GST account's full settings (for the settings edit form). */
export const useBillingEntityById = (id: string | null) =>
  useQuery({
    queryKey: ['billing-entity', id],
    queryFn: () => getBillingEntityById(id as string),
    enabled: Boolean(id),
  });

export const useCreateBillingEntity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBillingEntity) => createBillingEntity(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-entities'] });
      void qc.invalidateQueries({ queryKey: ['billing-entity'] });
      toast.success('GST account created');
    },
    onError: () => toast.error('Failed to create GST account'),
  });
};

export const useUpdateBillingEntityById = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateBillingEntitySettings }) =>
      updateBillingEntityById(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-entities'] });
      void qc.invalidateQueries({ queryKey: ['billing-entity'] });
      toast.success('GST account saved');
    },
    onError: () => toast.error('Failed to save GST account'),
  });
};

export const useSetDefaultBillingEntity = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => setDefaultBillingEntity(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-entities'] });
      toast.success('Default GST account updated');
    },
    onError: () => toast.error('Failed to update default GST account'),
  });
};
